import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type { FulfilmentDetailDto, FulfilmentDto, PrinterDto, WarehouseDto } from '@thebb/shared';
import { get, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, PageTitle, Spinner, fmtTime } from '../components/ui';
import { PackagesEditor } from './Packaging';
import { ShippingPanel } from '../components/Shipping';

export const fulfilmentStatusLabel: Record<string, string> = { planned: 'Planlagt', released: 'Frigivet', picking: 'Plukkes', picked: 'Plukket', packing: 'Pakkes', packed: 'Pakket', shipped: 'Sendt', cancelled: 'Annulleret' };
export const fulfilmentTone = (s: string) => (s === 'shipped' ? 'green' : s === 'cancelled' ? 'red' : s === 'released' ? 'blue' : 'amber') as 'green' | 'red' | 'blue' | 'amber';
const shortLabel: Record<string, string> = { not_found: 'Ikke fundet', damaged: 'Beskadiget', wrong_count: 'Forkert antal', other: 'Andet' };

/** Remembered per browser: the warehouse and the station's printers. */
function useStation() {
  const read = (k: string) => { try { return localStorage.getItem(k) ?? ''; } catch { return ''; } };
  const [warehouseId, setWarehouseIdState] = useState(() => read('station.warehouseId'));
  const [laserPrinterId, setLaserState] = useState(() => read('station.laserPrinterId'));
  const setWarehouseId = (v: string) => { setWarehouseIdState(v); try { localStorage.setItem('station.warehouseId', v); } catch { /* ignore */ } };
  const setLaserPrinterId = (v: string) => { setLaserState(v); try { localStorage.setItem('station.laserPrinterId', v); } catch { /* ignore */ } };
  return { warehouseId, setWarehouseId, laserPrinterId, setLaserPrinterId };
}

function StationBar({ station }: { station: ReturnType<typeof useStation> }) {
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  const printers = useQuery({ queryKey: ['printers'], queryFn: () => get<PrinterDto[]>('/printing/printers') });
  useEffect(() => {
    if (!station.warehouseId && warehouses.data?.length) station.setWarehouseId(warehouses.data.find((w) => w.type === 'operated')?.id ?? warehouses.data[0]!.id);
  }, [warehouses.data, station]);
  return (
    <>
      <select className="input w-40" value={station.warehouseId} onChange={(e) => station.setWarehouseId(e.target.value)}>
        {warehouses.data?.filter((w) => w.active).map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
      </select>
      <select className="input w-56" value={station.laserPrinterId} onChange={(e) => station.setLaserPrinterId(e.target.value)} title="Printer til plukseddel og følgeseddel">
        <option value="">Ingen printer (kun PDF)</option>
        {printers.data?.filter((p) => p.enabled && p.kind === 'pdf').map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}
      </select>
    </>
  );
}

function FulfilmentTable({ items, to }: { items: FulfilmentDto[]; to: '/pick/$id' | '/pack/$id' }) {
  return (
    <table className="table card p-0">
      <thead><tr><th>Nr.</th><th>Ordre</th><th>Shop</th><th>Kunde</th><th>Status</th><th className="text-right">Linjer</th><th className="text-right">Stk</th><th>Frigivet</th><th>Plukket af</th></tr></thead>
      <tbody>
        {items.map((f) => (
          <tr key={f.id}>
            <td><Link to={to} params={{ id: f.id }} className="font-medium text-blue-700 hover:underline">{f.number}</Link></td>
            <td><Link to="/orders/$id" params={{ id: f.orderId }} className="text-blue-700 hover:underline">{f.orderNumber}</Link></td>
            <td>{f.shopName}</td>
            <td>{f.customerName ?? '–'}</td>
            <td><Badge tone={fulfilmentTone(f.status)}>{fulfilmentStatusLabel[f.status]}</Badge></td>
            <td className="text-right tabular-nums">{f.lineCount}</td>
            <td className="text-right tabular-nums">{f.unitCount}</td>
            <td className="whitespace-nowrap">{fmtTime(f.releasedAt)}</td>
            <td>{f.pickedBy ?? ''}</td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={9} className="text-zinc-500">Intet at gøre her lige nu.</td></tr>}
      </tbody>
    </table>
  );
}

/** Pick: fulfilments released to my warehouse, oldest first. */
export function PickPage() {
  const station = useStation();
  const [q, setQ] = useState('');
  const params = useMemo(() => { const p = new URLSearchParams({ stage: 'pick', perPage: '100' }); if (station.warehouseId) p.set('warehouseId', station.warehouseId); if (q) p.set('q', q); return p.toString(); }, [station.warehouseId, q]);
  const list = useQuery({ queryKey: ['fulfilments', params], queryFn: () => get<{ items: FulfilmentDto[]; total: number }>(`/fulfilments?${params}`), refetchInterval: 15_000 });
  return (
    <div>
      <PageTitle title="Pluk" actions={<><input className="input w-48" placeholder="Søg nr., kunde, shop" value={q} onChange={(e) => setQ(e.target.value)} /><StationBar station={station} /></>} />
      {list.isLoading ? <Spinner /> : <FulfilmentTable items={list.data?.items ?? []} to="/pick/$id" />}
    </div>
  );
}

/** One fulfilment being picked: lines in walking order, quantity and location per line, shortages. */
export function PickDetailPage() {
  const { id } = useParams({ from: '/app/pick/$id' });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();
  const station = useStation();
  const [error, setError] = useState<unknown>(null);
  const detail = useQuery({ queryKey: ['fulfilment', id], queryFn: () => get<FulfilmentDetailDto>(`/fulfilments/${id}`) });
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['fulfilment', id] }); void qc.invalidateQueries({ queryKey: ['fulfilments'] }); void qc.invalidateQueries({ queryKey: ['stock'] }); };
  const start = useMutation({ mutationFn: () => post(`/fulfilments/${id}/start-picking`), onSuccess: invalidate, onError: setError });
  const pick = useMutation({ mutationFn: (v: { lineId: string; quantityPicked: number; locationId: string | null; shortReason?: string | null }) => post(`/fulfilments/${id}/lines/${v.lineId}/pick`, { quantityPicked: v.quantityPicked, locationId: v.locationId, shortReason: v.shortReason ?? null }), onSuccess: invalidate, onError: setError });
  const finish = useMutation({ mutationFn: () => post(`/fulfilments/${id}/finish-pick`), onSuccess: () => { invalidate(); void navigate({ to: '/pick' }); }, onError: setError });
  const printNote = useMutation({ mutationFn: () => post<{ documentId: string }>(`/fulfilments/${id}/pick-note`, station.laserPrinterId ? { printerId: station.laserPrinterId } : {}), onSuccess: (r) => { invalidate(); if (!station.laserPrinterId) window.open(`/api/documents/${r.documentId}/download`, '_blank'); }, onError: setError });
  const [draft, setDraft] = useState<Record<string, { qty: string; locationId: string; reason: string }>>({});
  const f = detail.data;
  if (!f) return <Spinner />;
  const d = (l: FulfilmentDetailDto['lines'][number]) => draft[l.id] ?? { qty: String(l.quantityPicked || l.quantity), locationId: l.locationId ?? l.pickFrom[0]?.locationId ?? '', reason: l.shortReason ?? 'not_found' };
  const allDone = f.lines.every((l) => l.quantityPicked > 0 || l.shortReason);
  return (
    <div className="space-y-4">
      <PageTitle
        title={`Pluk ${f.number}`}
        actions={
          <>
            <Link to="/pick" className="btn">Tilbage</Link>
            <button className="btn" disabled={printNote.isPending} onClick={() => printNote.mutate()}>{station.laserPrinterId ? 'Print plukseddel' : 'Plukseddel (PDF)'}</button>
            {f.status === 'released' && can('floor.pick') && <button className="btn btn-primary" disabled={start.isPending} onClick={() => start.mutate()}>Start pluk</button>}
            {f.status === 'picking' && can('floor.pick') && <button className="btn btn-primary" disabled={!allDone || finish.isPending} onClick={() => finish.mutate()}>Afslut pluk</button>}
          </>
        }
      />
      <ErrorBox error={error} />
      <div className="flex flex-wrap gap-3 text-sm">
        <Badge tone={fulfilmentTone(f.status)}>{fulfilmentStatusLabel[f.status]}</Badge>
        <span>Ordre <Link to="/orders/$id" params={{ id: f.orderId }} className="text-blue-700 hover:underline">{f.orderNumber}</Link></span>
        <span>{f.shopName}</span>
        {f.customerName && <span>{f.customerName}</span>}
        <span>{f.warehouseCode}</span>
        {f.pickedBy && <span>plukker: {f.pickedBy}</span>}
      </div>
      <table className="table card p-0">
        <thead><tr><th>Lokation</th><th>SKU</th><th>Vare</th><th className="text-right">Antal</th><th className="w-28 text-right">Plukket</th><th className="w-44">Fra lokation</th><th className="w-40">Mangel</th><th></th></tr></thead>
        <tbody>
          {f.lines.map((l) => {
            const v = d(l);
            const short = Number(v.qty) < l.quantity;
            const done = l.quantityPicked > 0 || !!l.shortReason;
            return (
              <tr key={l.id} className={done ? 'bg-green-50' : ''}>
                <td className="font-medium">{l.locationCode ?? l.pickFrom[0]?.locationCode ?? <span className="text-red-700">ingen beholdning</span>}</td>
                <td>{l.sku}</td>
                <td>{l.title}{l.variantTitle && <span className="text-zinc-500"> · {l.variantTitle}</span>}</td>
                <td className="text-right tabular-nums text-lg font-semibold">{l.quantity}</td>
                <td className="text-right">{f.status === 'picking' ? <input className="input text-right" type="number" min={0} max={l.quantity} value={v.qty} onChange={(e) => setDraft({ ...draft, [l.id]: { ...v, qty: e.target.value } })} /> : <span className="tabular-nums">{l.quantityPicked}</span>}</td>
                <td>
                  {f.status === 'picking' ? (
                    <select className="input" value={v.locationId} onChange={(e) => setDraft({ ...draft, [l.id]: { ...v, locationId: e.target.value } })}>
                      <option value="">vælg</option>
                      {l.pickFrom.map((p) => <option key={p.locationId} value={p.locationId}>{p.locationCode} ({p.onHand})</option>)}
                    </select>
                  ) : (l.locationCode ?? '')}
                </td>
                <td>{f.status === 'picking' && short ? <select className="input" value={v.reason} onChange={(e) => setDraft({ ...draft, [l.id]: { ...v, reason: e.target.value } })}>{Object.entries(shortLabel).map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select> : l.shortReason ? shortLabel[l.shortReason] : ''}</td>
                <td>{f.status === 'picking' && can('floor.pick') && <button className="btn" disabled={pick.isPending || (Number(v.qty) > 0 && !v.locationId)} onClick={() => pick.mutate({ lineId: l.id, quantityPicked: Number(v.qty), locationId: v.locationId || null, shortReason: short ? v.reason : null })}>OK</button>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Pack: picked fulfilments; mark packed, enter tracking, ship with the delivery note. */
export function PackPage() {
  const station = useStation();
  const [q, setQ] = useState('');
  const params = useMemo(() => { const p = new URLSearchParams({ stage: 'pack', perPage: '100' }); if (station.warehouseId) p.set('warehouseId', station.warehouseId); if (q) p.set('q', q); return p.toString(); }, [station.warehouseId, q]);
  const list = useQuery({ queryKey: ['fulfilments', params], queryFn: () => get<{ items: FulfilmentDto[]; total: number }>(`/fulfilments?${params}`), refetchInterval: 15_000 });
  return (
    <div>
      <PageTitle title="Pak" actions={<><input className="input w-48" placeholder="Søg nr., kunde, shop" value={q} onChange={(e) => setQ(e.target.value)} /><StationBar station={station} /></>} />
      {list.isLoading ? <Spinner /> : <FulfilmentTable items={list.data?.items ?? []} to="/pack/$id" />}
    </div>
  );
}

export function PackDetailPage() {
  const { id } = useParams({ from: '/app/pack/$id' });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();
  const station = useStation();
  const [error, setError] = useState<unknown>(null);
  const [tracking, setTracking] = useState('');
  const [palletChoice, setPallet] = useState<boolean | null>(null);
  const detail = useQuery({ queryKey: ['fulfilment', id], queryFn: () => get<FulfilmentDetailDto>(`/fulfilments/${id}`) });
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['fulfilment', id] }); void qc.invalidateQueries({ queryKey: ['fulfilments'] }); void qc.invalidateQueries({ queryKey: ['orders'] }); void qc.invalidateQueries({ queryKey: ['stock'] }); void qc.invalidateQueries({ queryKey: ['shipping-plan'] }); };
  const pack = useMutation({ mutationFn: () => post(`/fulfilments/${id}/pack`, {}), onSuccess: invalidate, onError: setError });
  const ship = useMutation({ mutationFn: () => post<FulfilmentDetailDto>(`/fulfilments/${id}/ship`, { trackingNumber: tracking || null, printerId: station.laserPrinterId || undefined }), onSuccess: (r) => { invalidate(); if (!station.laserPrinterId && r.deliveryNoteDocumentId) window.open(`/api/documents/${r.deliveryNoteDocumentId}/download`, '_blank'); void navigate({ to: '/pack' }); }, onError: setError });
  const note = useMutation({ mutationFn: () => post<{ documentId: string }>(`/fulfilments/${id}/delivery-note`, station.laserPrinterId ? { printerId: station.laserPrinterId } : {}), onSuccess: (r) => { invalidate(); if (!station.laserPrinterId) window.open(`/api/documents/${r.documentId}/download`, '_blank'); }, onError: setError });
  const f = detail.data;
  if (!f) return <Spinner />;
  const pallet = palletChoice ?? f.packages.some((p) => p.kind === 'pallet');
  return (
    <div className="space-y-4">
      <PageTitle
        title={`Pak ${f.number}`}
        actions={
          <>
            <Link to="/pack" className="btn">Tilbage</Link>
            <button className="btn" disabled={note.isPending} onClick={() => note.mutate()}>{station.laserPrinterId ? 'Print følgeseddel' : 'Følgeseddel (PDF)'}</button>
            {f.status === 'picked' && can('floor.pack') && <button className="btn" disabled={pack.isPending} onClick={() => pack.mutate()}>Markér pakket</button>}
            {['picked', 'packing', 'packed'].includes(f.status) && can('floor.pack') && <button className="btn btn-primary" disabled={ship.isPending} onClick={() => confirm(`Send ${f.number}? Lageret trækkes.`) && ship.mutate()}>Send</button>}
          </>
        }
      />
      <ErrorBox error={error} />
      <div className="flex flex-wrap gap-3 text-sm">
        <Badge tone={fulfilmentTone(f.status)}>{fulfilmentStatusLabel[f.status]}</Badge>
        <span>Ordre <Link to="/orders/$id" params={{ id: f.orderId }} className="text-blue-700 hover:underline">{f.orderNumber}</Link></span>
        <span>{f.shopName}</span>
        {f.customerName && <span>{f.customerName}</span>}
        {f.pickedBy && <span>plukket af {f.pickedBy} {fmtTime(f.pickedAt)}</span>}
        {f.packedBy && <span>pakket af {f.packedBy}</span>}
      </div>
      <div className="card">
        <div className="mb-2 flex items-center justify-between">
          <div className="label">{pallet ? 'Paller' : 'Kolli'}</div>
          {/* A single order can go as parcels or on pallets; saving one kind replaces the other. */}
          <div className="inline-flex overflow-hidden rounded-md border border-line text-sm">
            {([false, true] as const).map((v) => (
              <button key={String(v)} type="button" className={`px-3 py-1 ${pallet === v ? 'bg-primary text-white' : 'bg-white'}`} onClick={() => setPallet(v)}>{v ? 'Palle' : 'Kolli'}</button>
            ))}
          </div>
        </div>
        <PackagesEditor key={pallet ? 'pallet' : 'parcel'} ownerType="fulfilment" ownerId={f.id} packages={f.packages.filter((p) => (p.kind === 'pallet') === pallet)} contentWeightG={f.contentWeightG} pallets={pallet} disabled={!['picked', 'packing', 'packed'].includes(f.status) || !can('floor.pack')} onSaved={invalidate} onError={setError} />
      </div>
      {!f.batchId && <ShippingPanel ownerType="fulfilment" ownerId={f.id} laserPrinterId={station.laserPrinterId} disabled={!['picked', 'packing', 'packed'].includes(f.status)} onBooked={invalidate} onError={setError} />}
      {['picked', 'packing', 'packed'].includes(f.status) && (
        <div className="card flex flex-wrap items-end gap-3 text-sm">
          {!f.trackingNumber && <div className="w-72"><label className="label">Track & trace uden booking (valgfrit)</label><input className="input" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="hvis fragten er booket et andet sted" /></div>}
          <div className="text-xs text-muted">Book fragten først; Send bruger fragtbrevets nummer. Send bogfører pluk-bevægelser på de valgte lokationer og frigiver reservationerne.</div>
        </div>
      )}
      <table className="table card p-0">
        <thead><tr><th>SKU</th><th>Vare</th><th className="text-right">Bestilt</th><th className="text-right">Plukket</th><th>Lokation</th><th>Mangel</th></tr></thead>
        <tbody>
          {f.lines.map((l) => (
            <tr key={l.id} className={l.quantityPicked < l.quantity ? 'bg-amber-50' : ''}>
              <td>{l.sku}</td>
              <td>{l.title}{l.variantTitle && <span className="text-zinc-500"> · {l.variantTitle}</span>}</td>
              <td className="text-right tabular-nums">{l.quantity}</td>
              <td className="text-right tabular-nums font-semibold">{l.quantityPicked}</td>
              <td>{l.locationCode ?? ''}</td>
              <td>{l.shortReason ? shortLabel[l.shortReason] : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {f.trackingNumber && <div className="text-sm">Track & trace: {f.trackingNumber}</div>}
    </div>
  );
}
